import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { loadContent, ProductContent } from '../utils/contentLoader';
import './ProductDetailPage.css';

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<ProductContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Scroll animation observer setup
    if (!loading && product) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      const elementsToAnimate = document.querySelectorAll('.scroll-animate');
      elementsToAnimate.forEach((el) => observer.observe(el));

      return () => {
        elementsToAnimate.forEach((el) => observer.unobserve(el));
        observer.disconnect();
      };
    }
  }, [loading, product]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      
      try {
        const products = await loadContent('products', slug) as ProductContent[];
        
        if (products.length > 0) {
          setProduct(products[0]);
        } else {
          setError('Product not found');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product');
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container">
        <div className="error-message">
          <h2>{error || 'Product not found'}</h2>
          <p>The product you're looking for could not be found.</p>
          <Link to="/products" className="back-button">Back to Products</Link>
        </div>
      </div>
    );
  }

  // Split description into paragraphs if it exists
  const descriptionParagraphs = product.description ? product.description.split('\n\n').filter(p => p.trim()) : [];

  return (
    <div className="product-detail-page">
      {/* Full-width Header */}
      {product && (
        <header className="product-detail-page-header">
          <div className="container">
            <h1>{product.title}</h1>
            {/* Optional: <p className="page-description">Product Details</p> */}
          </div>
        </header>
      )}
      <div className="container">
        <nav aria-label="Breadcrumb" className="breadcrumbs">
          <ol>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/products">Products</Link></li>
            <li aria-current="page">{product.title}</li>
          </ol>
        </nav>

        <div className="product-detail">
          <div className="product-image-container">
            <img 
              src={product.image} 
              alt={product.title} 
              className="product-detail-image"
            />
          </div>
          
          <div className="product-info">
            
            <div className="product-tags scroll-animate fade-in-up-init">
              {product.tags && product.tags.map(tag => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
            
            <div className="product-description scroll-animate fade-in-up-init">
              {descriptionParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            
            {product.allergens && product.allergens.length > 0 && (
              <div className="allergens-section scroll-animate fade-in-up-init">
                <h2>Allergen Information</h2>
                <p>This product contains:</p>
                <ul className="allergen-list">
                  {product.allergens.map(allergen => (
                    <li key={allergen} className="allergen-item">{allergen}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {product.notes && (
              <div className="product-notes scroll-animate fade-in-up-init">
                <h2>Notes</h2>
                <p>{product.notes}</p>
              </div>
            )}
            
            <div className="ordering-info scroll-animate fade-in-up-init">
              <h2>How to Order</h2>
              <p>Interested in ordering this product? Contact us to place your order or inquire about customizations.</p>
              <Link to="/contact" className="order-button">Contact Us to Order</Link>
            </div>
          </div>
        </div>

        <div className="back-to-products">
          <Link to="/products" className="back-button">
            ← Back to Products
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
